import { Consent } from '../api/consent';
import { scrollToFieldWithError } from '../helpers/scrollToFieldWithError';
import { addOrUpdateFormField } from '../store/applicationform';
import { useAppDispatch } from '.';
import useValidation from './useValidation';

const PPI_VALIDATION_ERROR_MESSAGE = 'validation.payment-protection-insurance.required';

/**
 * Shared gate for the DK/NO PPI pages: requires an explicit yes/no answer to every actionable PPI consent
 * before `onAction` may proceed, regardless of the consent's own `required` flag -- unlike the credit-status
 * consent pages, this isn't about whether accepting is mandatory, it's about never silently recording "no
 * answer" as "declined". `INFORMATION` consents are excluded since they render no yes/no control at all --
 * including one here would make the page permanently impassable, matching EskatKreditStatusConsentPageDK.
 *
 * `hasConsentToAccept` also guards the loading/error window: `useGetConsentService` starts with
 * `consentDetails` undefined, so deriving the gate from `validationErrors` alone fails OPEN during that
 * window (and forever, if the fetch errors) -- there is nothing to validate, so nothing blocks `onAction`.
 *
 * `primaryConsent` is the single consent whose answer `acceptInsuranceService` actually submits --
 * `acceptInsuranceService` takes one boolean, so there is no way to submit more than one consent's answer.
 * Callers must read the customer's choice from `primaryConsent.name`, not by joining every consent's name
 * into one lookup key (that comma-joined string never matches any individual `RadioField`'s state).
 */
const usePpiConsentGate = (consentDetails: Consent[] | undefined, isLoading: boolean, isError: boolean) => {
    const dispatch = useAppDispatch();

    const consentsToAccept = consentDetails?.filter((consentDetail) => consentDetail.displayType !== 'INFORMATION');
    const consentFieldNames = consentsToAccept?.map((consentDetail) => consentDetail.name) ?? [];
    const validationErrors = useValidation(consentFieldNames);
    const hasConsentToAccept = !isLoading && !isError && !!consentsToAccept?.length;
    const primaryConsent = consentsToAccept?.[0];

    const canProceed = () => {
        if (!hasConsentToAccept) {
            return false;
        }

        if (validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(
                    addOrUpdateFormField({
                        ...validationError,
                        errorMessage: PPI_VALIDATION_ERROR_MESSAGE,
                    }),
                );
            });
            return false;
        }

        return true;
    };

    return { hasConsentToAccept, canProceed, primaryConsent };
};

export default usePpiConsentGate;
